import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Check, Plus, ShoppingCart, ArrowRight } from "lucide-react";
import { baseCourses, endorsementCourses, COURSE_PRICE, ENDORSEMENT_PRICE } from "../mock";
import { goToCheckout } from "../lib/checkout";

// Popup shown after a user picks one of the 3 base license classes.
// Lets them add any of the 3 endorsements, or continue without.
// On checkout it forwards base + selected endorsements to the external site.
export default function EndorsementModal({ open, onOpenChange, baseCourseId }) {
  const [addons, setAddons] = useState([]);

  // reset selections whenever a new base course opens the modal
  useEffect(() => {
    if (open) setAddons([]);
  }, [open, baseCourseId, setAddons]);

  const baseCourse = useMemo(
    () => baseCourses.find((c) => c.id === baseCourseId),
    [baseCourseId]
  );

  const toggleAddon = (id) =>
    setAddons((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const total = useMemo(
    () =>
      (baseCourse?.price || 0) +
      endorsementCourses
        .filter((c) => addons.includes(c.id))
        .reduce((sum, c) => sum + c.price, 0),
    [baseCourse, addons]
  );

  const checkout = (ids) => {
    onOpenChange(false);
    goToCheckout(ids);
  };

  if (!baseCourse) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <DialogHeader className="px-7 pt-7 pb-4 border-b border-slate-100">
          <DialogTitle className="font-display text-2xl text-brand text-left">
            Add Endorsements?
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-left">
            You selected{" "}
            <span className="text-slate-900 font-semibold">
              {baseCourse.name}
            </span>
            . Want to add any endorsements before you check out? Each is just
            ${ENDORSEMENT_PRICE}.
          </DialogDescription>
        </DialogHeader>

        <div className="px-7 py-5 space-y-3">
          {endorsementCourses.map((c) => {
            const active = addons.includes(c.id);
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => toggleAddon(c.id)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 ${
                  active
                    ? "border-brand bg-[#16365C]/5"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="w-11 h-11 shrink-0 rounded-lg bg-[#16365C]/8 flex items-center justify-center">
                  <Icon className={`w-6 h-6 ${active ? "text-brand" : "text-steel"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-semibold text-slate-900">
                    {c.name}
                  </div>
                  <div className="text-slate-400 text-xs">{c.tagline}</div>
                </div>
                <div className="font-bold text-brand shrink-0">+${c.price}</div>
                <div
                  className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center border-2 transition-colors"
                  style={{
                    background: active ? "#16365C" : "transparent",
                    borderColor: active ? "#16365C" : "#CBD5E1",
                  }}
                >
                  {active ? (
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  ) : (
                    <Plus className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-7 pb-7 pt-2">
          <div className="flex items-center justify-between mb-4">
            <span className="font-head font-semibold uppercase tracking-wide text-slate-500 text-sm">
              Total
            </span>
            <span className="font-display text-3xl text-brand">${total.toFixed(2)}</span>
          </div>

          <button
            onClick={() => checkout([baseCourse.id, ...addons])}
            className="btn-brand rounded-md w-full py-3.5 flex items-center justify-center gap-2"
          >
            <ShoppingCart className="w-5 h-5" />
            {addons.length
              ? `Proceed to Checkout \u2022 $${total.toFixed(2)}`
              : "Proceed to Checkout"}
          </button>

          <button
            onClick={() => checkout([baseCourse.id])}
            className="w-full mt-3 py-3 font-head font-semibold uppercase tracking-wide text-sm text-slate-500 hover:text-brand transition-colors inline-flex items-center justify-center gap-1.5"
          >
            Continue without endorsements <ArrowRight className="w-4 h-4" />
          </button>

          <p className="text-slate-400 text-[11px] text-center mt-3">
            Secure checkout &amp; payment handled at app.cdlacademy.com
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
